import React, { useMemo, useState } from "react";
import { useData } from "../../context/DataContext";
import OrgChartTree from "./OrgChartTree";
import { buildOrgChartTree } from "../../utils/orgChartTree";
import {
  createBranch,
  createDepartment,
  deleteDepartment,
  updateDepartment,
} from "../../api";
import { suggestBranchCode } from "../../utils/dataMappers";
import { useOrganizationStructure } from "../../hooks/useOrganizationStructure";
import { useIndustryLabels } from "../../context/IndustryContext";
import { isOperationsDepartment } from "../../utils/orgHelpers";
import "./HrDashboard.css";
import "./HrOrgStructure.css";

const PROTECTED_DELETE_CODES = new Set(["HR", "OPS", "ACC"]);

function suggestDepartmentCode(name) {
  const latin = name
    .trim()
    .replace(/[^\w\s]/g, "")
    .split(/\s+/)
    .map((w) => w.charAt(0))
    .join("")
    .toUpperCase()
    .slice(0, 6);
  return latin.length >= 2 ? latin : `D${Date.now().toString().slice(-4)}`;
}

function extractErrorMessage(err, fallback) {
  const message = err.response?.data?.message || err.response?.data?.error;
  if (Array.isArray(message)) return message.join("\n");
  return message || fallback;
}

function HrOrgStructure() {
  const { branches, departments, employeesByBranch, refreshData } = useData();
  const labels = useIndustryLabels();
  const {
    structures,
    selected: orgStructure,
    saving: savingStructure,
    error: structureError,
    selectStructure,
  } = useOrganizationStructure();

  const [showBranchForm, setShowBranchForm] = useState(false);
  const [newBranchName, setNewBranchName] = useState("");
  const [newBranchCode, setNewBranchCode] = useState("");
  const [creatingBranch, setCreatingBranch] = useState(false);
  const [branchError, setBranchError] = useState("");

  const [showDeptForm, setShowDeptForm] = useState(false);
  const [newDeptName, setNewDeptName] = useState("");
  const [newDeptCode, setNewDeptCode] = useState("");
  const [creatingDept, setCreatingDept] = useState(false);
  const [deptError, setDeptError] = useState("");

  const [editingDeptId, setEditingDeptId] = useState(null);
  const [editDeptName, setEditDeptName] = useState("");
  const [savingDeptId, setSavingDeptId] = useState(null);
  const [deletingDeptId, setDeletingDeptId] = useState(null);
  const [deptActionError, setDeptActionError] = useState("");

  const allEmployees = useMemo(
    () => Object.values(employeesByBranch || {}).flat(),
    [employeesByBranch],
  );

  const deptSummaries = useMemo(
    () =>
      departments
        .filter((dept) => dept.isActive !== false)
        .map((dept) => {
          const staff = allEmployees.filter(
            (e) =>
              (e.departmentId === dept.id ||
                (dept.backendId && e.departmentBackendId === dept.backendId)) &&
              e.status !== "terminated",
          );
          return {
            ...dept,
            staffCount: staff.length,
            isOps: isOperationsDepartment(dept.id),
            canDelete: !PROTECTED_DELETE_CODES.has(dept.code),
          };
        }),
    [departments, allEmployees],
  );

  const orgChart = useMemo(
    () =>
      buildOrgChartTree({
        structure: orgStructure,
        departments,
        branches,
        employees: allEmployees,
      }),
    [orgStructure, departments, branches, allEmployees],
  );

  const canManageBranches =
    Boolean(orgStructure?.usesBranches) ||
    departments.some(
      (dept) =>
        dept.isActive !== false && isOperationsDepartment(dept.id),
    );

  const branchSummaries = useMemo(
    () =>
      branches.map((branch) => {
        const employees = (employeesByBranch[branch.id] || []).filter(
          (emp) =>
            isOperationsDepartment(emp.departmentId) &&
            emp.status !== "terminated",
        );
        return {
          ...branch,
          total: employees.length,
          active: employees.filter((e) => e.status === "active").length,
        };
      }),
    [branches, employeesByBranch],
  );

  function startEditDepartment(dept) {
    setShowDeptForm(false);
    setDeptActionError("");
    setEditingDeptId(dept.id);
    setEditDeptName(dept.name);
  }

  function cancelEditDepartment() {
    setEditingDeptId(null);
    setEditDeptName("");
    setDeptActionError("");
  }

  async function handleSaveDepartmentName(dept) {
    const name = editDeptName.trim();
    setDeptActionError("");

    if (!name) {
      setDeptActionError("من فضلك أدخل اسم الإدارة");
      return;
    }
    if (name === dept.name) {
      cancelEditDepartment();
      return;
    }

    const backendId = dept.backendId || dept.id;
    setSavingDeptId(dept.id);
    try {
      await updateDepartment(backendId, { name });
      await refreshData();
      cancelEditDepartment();
    } catch (err) {
      setDeptActionError(
        extractErrorMessage(err, "تعذر تعديل اسم الإدارة"),
      );
    } finally {
      setSavingDeptId(null);
    }
  }

  async function handleDeleteDepartment(dept) {
    setDeptActionError("");

    if (!dept.canDelete) {
      setDeptActionError(
        "لا يمكن حذف الإدارات الأساسية (الموارد البشرية / التشغيل / الحسابات)",
      );
      return;
    }

    if (dept.staffCount > 0) {
      setDeptActionError(
        `لا يمكن حذف «${dept.name}» وبها ${dept.staffCount} موظف — انقل الموظفين أولاً`,
      );
      return;
    }

    const confirmed = window.confirm(
      `هل تريد حذف إدارة «${dept.name}»؟\nلن تظهر في الهيكل التنظيمي بعد الحذف.`,
    );
    if (!confirmed) return;

    const backendId = dept.backendId || dept.id;
    setDeletingDeptId(dept.id);
    try {
      await deleteDepartment(backendId);
      await refreshData();
      if (editingDeptId === dept.id) cancelEditDepartment();
    } catch (err) {
      setDeptActionError(extractErrorMessage(err, "تعذر حذف الإدارة"));
    } finally {
      setDeletingDeptId(null);
    }
  }

  async function handleSelectStructure(structure) {
    if (!structure || structure.id === orgStructure?.id || savingStructure) return;

    const confirmed = window.confirm(
      `تفعيل هيكل «${structure.name}»؟\nهيظهر في إضافة الموظفين الرتب والوحدات الخاصة بالهيكل ده. الموظفين والفروع الحاليين مش هيتمسحوا.`,
    );
    if (!confirmed) return;

    try {
      await selectStructure(structure.id);
    } catch {
      // The hook stores the message for the page.
    }
  }

  async function handleCreateBranch() {
    const name = newBranchName.trim();
    const code = newBranchCode.trim().toUpperCase() || suggestBranchCode(name);
    setBranchError("");

    if (!name) {
      setBranchError(`من فضلك أدخل اسم ${labels.site}`);
      return;
    }
    if (!/^[A-Z0-9_]{2,10}$/.test(code)) {
      setBranchError(
        `كود ${labels.site}: 2-10 أحرف/أرقام بالإنجليزية (مثل MAADI)`,
      );
      return;
    }

    setCreatingBranch(true);
    try {
      await createBranch({ name, code });
      await refreshData();
      setNewBranchName("");
      setNewBranchCode("");
      setShowBranchForm(false);
    } catch (err) {
      setBranchError(extractErrorMessage(err, `تعذر إنشاء ${labels.site}`));
    } finally {
      setCreatingBranch(false);
    }
  }

  async function handleCreateDepartment() {
    const name = newDeptName.trim();
    const code = newDeptCode.trim().toUpperCase() || suggestDepartmentCode(name);
    setDeptError("");

    if (!name) {
      setDeptError("من فضلك أدخل اسم الإدارة");
      return;
    }
    if (!/^[A-Z0-9_]{2,10}$/.test(code)) {
      setDeptError("كود الإدارة: 2-10 أحرف/أرقام بالإنجليزية (مثل SALES)");
      return;
    }

    setCreatingDept(true);
    try {
      await createDepartment({ name, code });
      await refreshData();
      setNewDeptName("");
      setNewDeptCode("");
      setShowDeptForm(false);
    } catch (err) {
      setDeptError(extractErrorMessage(err, "تعذر إنشاء الإدارة"));
    } finally {
      setCreatingDept(false);
    }
  }

  return (
    <div className="hr-dashboard hr-org-structure" dir="rtl">
      <div className="hr-header">
        <h1 className="hr-title">الهيكل التنظيمي</h1>
        <p className="hr-subtitle">
          اختار الهيكل المناسب لطريقة شغل {labels.entity}، وبعدين أضف الإدارات
          {canManageBranches ? ` و${labels.sitePlural}` : ""} من هنا
        </p>
      </div>

      <div className="hr-section">
        <div className="hr-section-header">
          <h2 className="hr-section-title">الهياكل التنظيمية</h2>
        </div>
        <p className="hr-section-hint">
          فعّل هيكل واحد حسب احتياج {labels.entity}. الاختيار يحدد الرتب المتاحة
          عند إضافة الموظفين، وهل {labels.entity} بتشتغل ب
          {labels.sitePlural} ميدانية ولا لأ.
        </p>
        {structureError && (
          <p className="hr-org-error hr-org-action-error">{structureError}</p>
        )}
        <div className="hr-org-structure-picker">
          {structures.map((structure) => {
            const isSelected = structure.id === orgStructure?.id;
            return (
              <button
                key={structure.id}
                type="button"
                className={`hr-org-structure-card${isSelected ? " is-selected" : ""}`}
                onClick={() => handleSelectStructure(structure)}
                disabled={savingStructure}
                aria-pressed={isSelected}
              >
                <div className="hr-org-structure-card-top">
                  <span className="hr-org-structure-name">{structure.name}</span>
                  {isSelected && (
                    <span className="hr-org-structure-badge">المفعّل</span>
                  )}
                </div>
                <p className="hr-org-structure-summary">{structure.summary}</p>
                <p className="hr-org-structure-fit">{structure.fit}</p>
                <div className="hr-org-levels">
                  {(structure.levels || []).map((level, index) => (
                    <span key={level} className="hr-org-level">
                      {index > 0 && <span className="hr-org-level-arrow">←</span>}
                      {level}
                    </span>
                  ))}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <div className="hr-section">
        <div className="hr-section-header">
          <h2 className="hr-section-title">شجرة {orgStructure?.name || "الهيكل"}</h2>
        </div>
        <p className="hr-section-hint">
          الشجرة بترسم شكل الهيكل المفعّل من الإدارات والفروع والرتب الموجودة
          حالياً.
        </p>
        <OrgChartTree tree={orgChart} />
      </div>

      <div className="hr-section">
        <div className="hr-section-header">
          <h2 className="hr-section-title">الإدارات</h2>
          <button
            type="button"
            className="hr-tab"
            onClick={() => {
              cancelEditDepartment();
              setShowDeptForm((v) => !v);
              setDeptError("");
            }}
          >
            {showDeptForm ? "إلغاء" : "+ إضافة إدارة"}
          </button>
        </div>
        <p className="hr-section-hint">
          يمكنك تعديل اسم أي إدارة أو حذف الإدارات الفارغة. الإدارات الأساسية
          (الموارد البشرية، التشغيل، الحسابات) لا تُحذف.
        </p>

        {showDeptForm && (
          <div className="hr-org-form">
            <div className="hr-org-form-grid">
              <label>
                اسم الإدارة
                <input
                  type="text"
                  value={newDeptName}
                  onChange={(e) => {
                    setNewDeptName(e.target.value);
                    if (!newDeptCode.trim()) {
                      setNewDeptCode(suggestDepartmentCode(e.target.value));
                    }
                  }}
                  placeholder="مثال: المبيعات"
                />
              </label>
              <label>
                كود الإدارة
                <input
                  type="text"
                  value={newDeptCode}
                  onChange={(e) =>
                    setNewDeptCode(e.target.value.toUpperCase())
                  }
                  placeholder="SALES"
                />
              </label>
            </div>
            {deptError && <p className="hr-org-error">{deptError}</p>}
            <button
              type="button"
              className="hr-tab is-active"
              onClick={handleCreateDepartment}
              disabled={creatingDept}
            >
              {creatingDept ? "جاري الحفظ..." : "حفظ الإدارة"}
            </button>
          </div>
        )}

        {deptActionError && (
          <p className="hr-org-error hr-org-action-error">{deptActionError}</p>
        )}

        <div className="hr-org-grid">
          {deptSummaries.length === 0 ? (
            <p className="hr-org-empty">لا توجد إدارات مسجلة</p>
          ) : (
            deptSummaries.map((dept) => {
              const isEditing = editingDeptId === dept.id;
              const isBusy =
                savingDeptId === dept.id || deletingDeptId === dept.id;

              return (
                <div key={dept.id} className="hr-org-card">
                  {isEditing ? (
                    <div className="hr-org-edit-row">
                      <input
                        type="text"
                        className="hr-org-edit-input"
                        value={editDeptName}
                        onChange={(e) => setEditDeptName(e.target.value)}
                        disabled={isBusy}
                        autoFocus
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            handleSaveDepartmentName(dept);
                          }
                          if (e.key === "Escape") cancelEditDepartment();
                        }}
                      />
                      <div className="hr-org-card-actions">
                        <button
                          type="button"
                          className="hr-org-action-btn is-save"
                          onClick={() => handleSaveDepartmentName(dept)}
                          disabled={isBusy}
                        >
                          {savingDeptId === dept.id ? "..." : "حفظ"}
                        </button>
                        <button
                          type="button"
                          className="hr-org-action-btn"
                          onClick={cancelEditDepartment}
                          disabled={isBusy}
                        >
                          إلغاء
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="hr-org-card-top">
                        <div className="hr-org-card-name">{dept.name}</div>
                        <div className="hr-org-card-actions">
                          <button
                            type="button"
                            className="hr-org-action-btn"
                            onClick={() => startEditDepartment(dept)}
                            disabled={isBusy}
                            title="تعديل الاسم"
                          >
                            تعديل
                          </button>
                          <button
                            type="button"
                            className="hr-org-action-btn is-danger"
                            onClick={() => handleDeleteDepartment(dept)}
                            disabled={isBusy || !dept.canDelete}
                            title={
                              dept.canDelete
                                ? "حذف الإدارة"
                                : "إدارة أساسية — لا يمكن حذفها"
                            }
                          >
                            {deletingDeptId === dept.id ? "..." : "حذف"}
                          </button>
                        </div>
                      </div>
                      <div className="hr-org-card-meta">
                        <span>الكود: {dept.code || "—"}</span>
                        <span>{dept.staffCount} موظف</span>
                        {dept.isOps && canManageBranches && (
                          <span className="hr-org-ops-tag">تشغيل — فروع</span>
                        )}
                      </div>
                    </>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>

      {canManageBranches ? (
      <div className="hr-section">
        <div className="hr-section-header">
          <h2 className="hr-section-title">
            {labels.sitePlural} إدارة التشغيل
          </h2>
          <button
            type="button"
            className="hr-tab"
            onClick={() => {
              setShowBranchForm((v) => !v);
              setBranchError("");
            }}
          >
            {showBranchForm ? "إلغاء" : `+ إضافة ${labels.site}`}
          </button>
        </div>
        <p className="hr-section-hint">
          {labels.sitePlural} ميدانية وتتبع إدارة التشغيل فقط. بعد الإنشاء يمكن
          تعيين الموظفين و{labels.siteManager} من شاشة الموظفين.
        </p>

        {showBranchForm && (
          <div className="hr-org-form">
            <div className="hr-org-form-grid">
              <label>
                اسم {labels.site}
                <input
                  type="text"
                  value={newBranchName}
                  onChange={(e) => {
                    setNewBranchName(e.target.value);
                    if (!newBranchCode.trim()) {
                      setNewBranchCode(suggestBranchCode(e.target.value));
                    }
                  }}
                  placeholder={`مثال: ${labels.site} التجمع`}
                />
              </label>
              <label>
                كود {labels.site}
                <input
                  type="text"
                  value={newBranchCode}
                  onChange={(e) =>
                    setNewBranchCode(e.target.value.toUpperCase())
                  }
                  placeholder="TAGAMOA"
                />
              </label>
            </div>
            {branchError && <p className="hr-org-error">{branchError}</p>}
            <button
              type="button"
              className="hr-tab is-active"
              onClick={handleCreateBranch}
              disabled={creatingBranch}
            >
              {creatingBranch ? "جاري الحفظ..." : `حفظ ${labels.site}`}
            </button>
          </div>
        )}

        <div className="hr-table-wrap">
          <table className="hr-table">
            <thead>
              <tr>
                <th>{labels.site}</th>
                <th>الكود</th>
                <th>إجمالي موظفي التشغيل</th>
                <th>نشط</th>
              </tr>
            </thead>
            <tbody>
              {branchSummaries.length === 0 ? (
                <tr>
                  <td colSpan={4}>لا توجد {labels.sitePlural} مسجلة</td>
                </tr>
              ) : (
                branchSummaries.map((branch) => (
                  <tr key={branch.id}>
                    <td>{branch.name}</td>
                    <td>{branch.code || "—"}</td>
                    <td>{branch.total}</td>
                    <td>{branch.active}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      ) : (
        <p className="hr-section-hint hr-org-structure-note">
          الهيكل المفعّل لا يستخدم فروعاً ميدانية. أي فروع مسجلة قبل كده تفضل
          موجودة ومش هتتمسح.
        </p>
      )}
    </div>
  );
}

export default HrOrgStructure;
